import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazyWithReload } from './lib/lazyWithReload';
import posthog from 'posthog-js';
import { AuthProvider } from './contexts/AuthContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { TaskProvider } from './contexts/TaskContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { BillingProvider } from './contexts/BillingContext';
import { BookCallProvider } from './contexts/BookCallContext';
import AccountStrips from './components/billing/AccountStrips';
import ProtectedRoute from './components/ProtectedRoute';
import PageLoader from './components/PageLoader';
import Sidebar, { SIDEBAR_W_EXPANDED, SIDEBAR_W_COLLAPSED, SIDEBAR_MIN_W, SIDEBAR_MAX_W } from './components/Sidebar';
import ConnectorsModalProvider from './components/connectors/ConnectorsModalProvider';
import ConnectorsModalHost from './components/connectors/ConnectorsModalHost';
import { CoachMarkProvider } from './components/coach/CoachMarkProvider';
import { CoachMarkHost } from './components/coach/CoachMarkHost';
import { CoachMilestoneBridge } from './components/coach/CoachMilestoneBridge';
import ChatRedirect from './components/ChatRedirect';
import { CHAT_MODE_HIDDEN } from './lib/workspaceMode';
import { captureAttribution } from './lib/attribution';
import { scrubPosthogEvent } from './lib/urlScrub';
import { captureSignupInvite } from './lib/signupInvite';
import { initClientMessaging } from './lib/clientMessaging';
import AnalyticsPageBridge from './components/AnalyticsPageBridge';
import { useIsMobile } from './hooks/useIsMobile';
import './App.css';

const SIDEBAR_STORAGE_KEY = 'fd:sidebar-collapsed';

// Never init on local hosts — the shared "Founding.dev" PostHog project holds
// PRODUCTION data for both the marketing site and this app; a local dev
// session must not pollute it (the landing repo learned this the hard way:
// a localhost replay session reached production data on 2026-07-31).
const isLocalHost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

if (process.env.REACT_APP_POSTHOG_KEY && !isLocalHost) {
  posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com',
    autocapture: false,
    // 'history_change': SPA route changes fire $pageview too (plain `true`
    // only captures the initial load in a CRA/react-router app).
    capture_pageview: 'history_change',
    // Server-identified only — a person becomes a PostHog "person" at
    // identify() (login), not on every anonymous pageview. Keeps event
    // volume/billing tied to real users instead of every anonymous visitor.
    person_profiles: 'identified_only',
    // The shared project has session replay ON with unmasked inputs (right
    // for the marketing site). In the app, what people type is their
    // business — task prompts, company data — so mask every input in
    // replays recorded from this surface.
    session_recording: { maskAllInputs: true },
    // The first pageview can fire before /connectors/callback takes its
    // one-time code and state out of the address bar (lib/urlScrub.ts).
    before_send: scrubPosthogEvent,
  });
  // Marketing site + app share ONE PostHog project. `surface` is the standard
  // dimension for filtering insights to one side; registering it here also
  // overwrites any `surface: 'marketing'` inherited through the shared
  // .founding.dev cookie the moment the app boots.
  posthog.register({ surface: 'app' });
}

// Merge the current URL/referrer into the shared cross-repo attribution
// cookies once per app boot (module-level, alongside posthog.init above —
// re-running per client-side route change would just re-check an unchanged
// URL). See lib/attribution.ts for the cookie contract.
captureAttribution();
// Stash a signup invite link's ?invite=<code> until someone is signed in to
// redeem it (AuthContext). See lib/signupInvite.ts.
captureSignupInvite();

// Customer.io snippet + Intercom loader (identified at login via
// AuthContext; launcher visibility handled by AnalyticsPageBridge).
initClientMessaging();

const AuthPage = lazyWithReload(() => import('./pages/AuthPage'));
const Dashboard = lazyWithReload(() => import('./pages/Dashboard'));
const ChatPage = lazyWithReload(() => import('./pages/ChatPage'));
const Projects = lazyWithReload(() => import('./pages/Projects'));
const CreateProject = lazyWithReload(() => import('./pages/CreateProject'));
const ImportPage = lazyWithReload(() => import('./pages/ImportPage'));
const ProjectDetails = lazyWithReload(() => import('./pages/ProjectDetails'));
const FormSubmissions = lazyWithReload(() => import('./pages/FormSubmissions'));
const Tasks = lazyWithReload(() => import('./pages/Tasks'));
const TaskDetailPage = lazyWithReload(() => import('./pages/TaskDetailPage'));
const Settings = lazyWithReload(() => import('./pages/Profile'));
const Connectors = lazyWithReload(() => import('./pages/Connectors'));
const Memory = lazyWithReload(() => import('./pages/Memory'));
const Templates = lazyWithReload(() => import('./pages/Templates'));
const Team = lazyWithReload(() => import('./pages/Team'));
const OpsMoonlightAdmin = lazyWithReload(() => import('./pages/OpsMoonlightAdmin'));
const OpsMoonlightDashboard = lazyWithReload(() => import('./pages/OpsMoonlightDashboard'));
const OpsMoonlightMachines = lazyWithReload(() => import('./pages/OpsMoonlightMachines'));
const OpsMoonlightBilling = lazyWithReload(() => import('./pages/OpsMoonlightBilling'));
const Plans = lazyWithReload(() => import('./pages/Plans'));
const OpsMoonlightInvites = lazyWithReload(() => import('./pages/OpsMoonlightInvites'));
const OpsMoonlightReferralProgram = lazyWithReload(() => import('./pages/OpsMoonlightReferralProgram'));
const Referrals = lazyWithReload(() => import('./pages/Referrals'));
const AcceptInvite = lazyWithReload(() => import('./pages/AcceptInvite'));
const AuthCallback = lazyWithReload(() => import('./pages/AuthCallback'));
const ConnectorCallback = lazyWithReload(() => import('./pages/ConnectorCallback'));
const Onboarding = lazyWithReload(() => import('./pages/Onboarding'));
const WaitlistWall = lazyWithReload(() => import('./pages/WaitlistWall'));
const NotFound = lazyWithReload(() => import('./pages/NotFound'));

const MOBILE_BAR_H = 52;

const AppContent: React.FC = () => {
  const location = useLocation();
  const isTaskDetailPage = location.pathname.startsWith('/tasks/') && location.pathname !== '/tasks';
  const isOnboardingPage = location.pathname === '/onboarding' || location.pathname === '/onboarding/subscription';
  const isAuthPage = location.pathname.startsWith('/auth');
  const isWaitlistPage = location.pathname === '/access' || location.pathname === '/waitlist';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const showSidebar = !isOnboardingPage && !isAuthPage && !isTaskDetailPage && !isWaitlistPage;

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const saved = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (saved === 'true') return true;
    if (saved === 'false') return false;
    return window.innerWidth < 1024;
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarCollapsed));
    } catch { /* localStorage may be unavailable in privacy modes */ }
  }, [sidebarCollapsed]);

  // The chat file preview (ChatPage) asks the sidebar to collapse while it's
  // open — gives the player room — and to restore the user's prior state on
  // close. collapsedRef tracks the live value so the listener binds once.
  const collapsedRef = useRef(sidebarCollapsed);
  collapsedRef.current = sidebarCollapsed;
  const preFilePanelCollapse = useRef<boolean | null>(null);
  useEffect(() => {
    const onFilePanel = (e: Event) => {
      const open = (e as CustomEvent).detail && (e as CustomEvent).detail.open;
      if (open) {
        if (preFilePanelCollapse.current === null) preFilePanelCollapse.current = collapsedRef.current;
        setSidebarCollapsed(true);
      } else if (preFilePanelCollapse.current !== null) {
        setSidebarCollapsed(preFilePanelCollapse.current);
        preFilePanelCollapse.current = null;
      }
    };
    window.addEventListener('founding:filePanel', onFilePanel as EventListener);
    return () => window.removeEventListener('founding:filePanel', onFilePanel as EventListener);
  }, []);

  // User-adjustable expanded sidebar width (drag handle on its right edge).
  // Lives here (not in Sidebar) because it also drives <main>'s padding-left.
  // Clamped on read so a stale/garbage stored value can't wedge the layout.
  const [sidebarExpandedWidth, setSidebarExpandedWidth] = useState<number>(() => {
    try {
      // Key versioned on 2026-08-24 with the narrower default. The old key
      // held widths up to 400; leaving it read would keep every existing user
      // on the width they last dragged to and hide the change completely.
      const v = parseInt(window.localStorage.getItem('fd:sidebar-width-v2') || '', 10);
      if (!Number.isNaN(v)) return Math.min(Math.max(v, SIDEBAR_MIN_W), SIDEBAR_MAX_W);
    } catch { /* localStorage may be unavailable in privacy modes */ }
    return SIDEBAR_W_EXPANDED;
  });
  const [sidebarResizing, setSidebarResizing] = useState(false);
  const handleSidebarResize = useCallback((w: number) => {
    setSidebarExpandedWidth(w);
    try { window.localStorage.setItem('fd:sidebar-width-v2', String(w)); } catch { /* ignore */ }
  }, []);

  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const sidebarWidth = sidebarCollapsed ? SIDEBAR_W_COLLAPSED : sidebarExpandedWidth;
  const desktopPaddingLeft = showSidebar && !isMobile ? sidebarWidth : 0;
  const mobilePaddingTop = showSidebar && isMobile ? MOBILE_BAR_H : 0;

  return (
    <div className="min-h-dvh bg-black">
      {showSidebar && (
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((c) => !c)}
          isMobile={isMobile}
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
          onMobileOpen={() => setMobileMenuOpen(true)}
          expandedWidth={sidebarExpandedWidth}
          onResize={handleSidebarResize}
          onResizingChange={setSidebarResizing}
        />
      )}

      {/* Mobile top bar — hamburger + logo, only on small viewports */}
      {showSidebar && isMobile && (
        <header
          className="fixed top-0 left-0 right-0 z-30 flex items-center gap-3 px-4"
          style={{
            height: MOBILE_BAR_H,
            background: '#0a0a0a',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <button
            type="button"
            aria-label="Open navigation"
            onClick={() => setMobileMenuOpen(true)}
            className="flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
            style={{ width: 44, height: 44 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <svg width="20" height="20" viewBox="0 0 34.95 40.29" aria-hidden="true" style={{ flexShrink: 0 }}>
            <defs>
              <linearGradient id="mob-logo-grad" x1="0" y1="20.15" x2="34.95" y2="20.15" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#2f69fa" />
                <stop offset=".36" stopColor="#6d49e7" />
                <stop offset=".52" stopColor="#a04ebd" />
                <stop offset=".66" stopColor="#db538c" />
                <stop offset=".71" stopColor="#f1567b" />
                <stop offset="1" stopColor="#fe813b" />
              </linearGradient>
            </defs>
            <path fill="url(#mob-logo-grad)" d="M32.35,15.75L6.02.54C3.34-1,0,.93,0,4.02v31.97c0,3.31,3.58,5.38,6.45,3.72l25.9-14.95c3.47-2,3.47-7,0-9Z" />
          </svg>
          <span className="text-white font-bold text-sm" style={{ letterSpacing: '-0.02em' }}>Founding.dev</span>
        </header>
      )}

      {/* The content sheet.
          Sidebar and shell are both pure black; the sheet sits one neutral step
          above them with a rounded top-left corner, so the boundary is drawn by
          geometry and elevation rather than by a colour anyone has to strain to
          see. Before this, sidebar #000 against canvas #0a0a0a measured 1.07:1
          — the same "effectively invisible" failure the marketing site's design
          notes call out — with a 5%-white hairline doing all the work.

          It is the horizontal reading of the stacked-sheet seam the marketing
          site uses between bands: the raised sheet's curve reveals the darker
          plane behind it. No accent colour is involved, so nothing is brand.

          MARGIN, not padding: padding would paint the sheet's background across
          the full viewport including the strip under the sidebar, and put the
          corner radius off-screen at x=0. Content width is identical either
          way — with padding-left the content box already started at the
          sidebar's edge. Not clipped (`overflow` stays visible) because pages
          inside rely on sticky positioning. */}
      <main
        style={{
          marginLeft: desktopPaddingLeft,
          paddingTop: mobilePaddingTop,
          minHeight: '100dvh',
          background: '#0a0a0a',
          ...(desktopPaddingLeft
            ? {
                borderLeft: '1px solid rgba(255, 255, 255, 0.07)',
                borderTopLeftRadius: 20,
              }
            : null),
          // No transition mid-drag — the content must track the handle 1:1.
          transition: sidebarResizing ? 'none' : 'margin-left 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Payment-failed, plan-ending and add-email strips (pricing gate T13). Not on
            the full-screen task page, whose layout owns the viewport. */}
        {showSidebar && <AccountStrips />}
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Build-first landing: the app opens on the Build surface (/apps). */}
          <Route path="/" element={<Navigate to="/apps" replace />} />

          {/* Auth */}
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/onboarding" element={<Onboarding />} />

          {/* No-access screen — standalone (NOT under ProtectedRoute, which would
              loop). The page self-guards: no user → /auth, allowed → /dashboard.
              /waitlist is the old address, kept so a bookmark still lands. */}
          <Route path="/access" element={<WaitlistWall />} />
          <Route path="/waitlist" element={<Navigate to="/access" replace />} />

          {/* Protected routes */}
          {/* Chat (the general-AI, Perplexity-style surface). Entry is /chat;
              a session lives at /chat/:id. */}
          {/* Hidden for launch (CHAT_MODE_HIDDEN): both land on /apps. */}
          <Route path="/chat" element={<ProtectedRoute />}>
            <Route index element={CHAT_MODE_HIDDEN ? <ChatRedirect /> : <ChatPage />} />
            <Route path=":id" element={CHAT_MODE_HIDDEN ? <ChatRedirect /> : <ChatPage />} />
          </Route>
          {/* Legacy chat routes → /chat (old bookmarks, open tabs, persisted
              links). /chats/:id and /dashboard/:id interpolate the id via
              ChatRedirect; the bare paths bounce to the chat home. */}
          <Route path="/chats" element={<ProtectedRoute />}>
            <Route index element={<ChatRedirect />} />
            <Route path=":id" element={<ChatRedirect />} />
          </Route>
          <Route path="/dashboard" element={<ProtectedRoute />}>
            <Route index element={<ChatRedirect />} />
            <Route path=":id" element={<ChatRedirect />} />
          </Route>
          {/* Apps = the app-builder composer (Dashboard component) + apps; /apps/all = full library */}
          <Route path="/apps" element={<ProtectedRoute />}>
            <Route index element={<Dashboard />} />
            <Route path="all" element={<Projects />} />
            {/* /apps/new used to gate every import behind a dedicated page,
                then a modal, then the Import tab on the Home composer. It is
                the /import page now (2026-10-30). CreateProject itself is NOT
                deleted: its create-new-repo mode and up-front environment
                variables still live at /apps/new/advanced (footer "More
                options" link). App URL + deployment tracking were removed
                from it 2026-09-10 (sprint card "Useless configs when
                creating a new app"); they remain editable in app settings. */}
            <Route path="new" element={<Navigate to="/import" replace />} />
            <Route path="new/advanced" element={<CreateProject />} />
            <Route path=":id" element={<ProjectDetails />} />
            <Route path=":id/forms" element={<FormSubmissions />} />
          </Route>
          {/* Bring in an app from GitHub: paste a link or pick a repo, and it
              opens in a task. Reached from the Home composer's + menu. */}
          <Route path="/import" element={<ProtectedRoute />}>
            <Route index element={<ImportPage />} />
          </Route>
          <Route path="/tasks" element={<ProtectedRoute />}>
            <Route index element={<Tasks />} />
            <Route path=":taskId" element={<TaskDetailPage />} />
          </Route>
          {/* Pricing gate (T13): the single offer card + code redeem. */}
          <Route path="/plans" element={<ProtectedRoute />}>
            <Route index element={<Plans />} />
          </Route>
          {/* Refer & earn: the user's referral link and progress (counts only, no identities). */}
          <Route path="/referrals" element={<ProtectedRoute />}>
            <Route index element={<Referrals />} />
          </Route>
          <Route path="/settings" element={<ProtectedRoute />}>
            <Route index element={<Settings />} />
          </Route>
          <Route path="/connectors" element={<ProtectedRoute />}>
            <Route index element={<Connectors />} />
            {/* Where Stripe / Notion / Linear send the browser back after
                their consent page (bring-your-own connectors). */}
            <Route path="callback" element={<ConnectorCallback />} />
          </Route>
          <Route path="/team" element={<ProtectedRoute />}>
            <Route index element={<Team />} />
          </Route>
          <Route path="/memory" element={<ProtectedRoute />}>
            <Route index element={<Memory />} />
          </Route>
          <Route path="/templates" element={<ProtectedRoute />}>
            <Route index element={<Templates />} />
          </Route>
          {/* Ops admin — staff only; the page self-guards (non-staff → /apps). Obscure slug on purpose. */}
          <Route path="/ops-moonlight-admin" element={<ProtectedRoute />}>
            <Route index element={<OpsMoonlightDashboard />} />
            <Route path="users" element={<OpsMoonlightAdmin />} />
            <Route path="machines" element={<OpsMoonlightMachines />} />
            <Route path="credit" element={<OpsMoonlightBilling />} />
            <Route path="invites" element={<OpsMoonlightInvites />} />
            {/* The badge-referrals page was folded into Referrals; old links land on its badge filter. */}
            <Route path="referrals" element={<Navigate to="/ops-moonlight-admin/referral-program?tab=list&source=badge" replace />} />
            <Route path="referral-program" element={<OpsMoonlightReferralProgram />} />
          </Route>
          {/* Invitee landing — public so a new user can reach it before signing in. */}
          <Route path="/invite/:token" element={<AcceptInvite />} />

          {/* Legacy redirects */}
          <Route path="/projects" element={<Navigate to="/apps" replace />} />
          <Route path="/projects/new" element={<Navigate to="/apps/new" replace />} />
          <Route path="/projects/:id" element={<Navigate to="/apps/:id" replace />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
    </div>
  );
};

function App() {
  // Stops the browser navigating to a file dropped outside a drop zone (the
  // tab replaces itself with the raw file). Bubble phase, no
  // stopPropagation, never sets dropEffect — composer-local handlers still
  // run first at target phase and this only mops up whatever they don't
  // claim.
  useEffect(() => {
    const preventDefault = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);
    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, []);

  return (
    <div className="App">
      <Router>
        {/* Route-change bridge for Customer.io page events + Intercom
            launcher visibility (see lib/clientMessaging.ts). Renders nothing. */}
        <AnalyticsPageBridge />
        <NotificationProvider>
          <AuthProvider>
            <WebSocketProvider>
              <ProjectProvider>
                <TaskProvider>
                  <BillingProvider>
                  {/* ConnectorsModalProvider sits below the routing/state
                      providers (it needs useSearchParams + useNotification)
                      but ABOVE the Routes so the modal survives route
                      changes. The Host mounts the actual modal UI; placing
                      it as a sibling of AppContent means the modal layers
                      over any page without each page having to mount it.
                      CoachMarkProvider nests the same way, one level in —
                      "Show me around" also needs useLocation/useNavigate to
                      survive route changes across an entire tour. */}
                  <ConnectorsModalProvider>
                    <CoachMarkProvider>
                    {/* "Talk to us" anywhere opens the Kalendar booking
                        dialog over the current page (contexts/BookCallContext). */}
                    <BookCallProvider>
                      <AppContent />
                      <ConnectorsModalHost />
                      <CoachMarkHost />
                      {/* Turns build/publish events into once-per-account
                          milestones for the tour's nudges. Renders nothing. */}
                      <CoachMilestoneBridge />
                    </BookCallProvider>
                    </CoachMarkProvider>
                  </ConnectorsModalProvider>
                  </BillingProvider>
                </TaskProvider>
              </ProjectProvider>
            </WebSocketProvider>
          </AuthProvider>
        </NotificationProvider>
      </Router>
    </div>
  );
}

export default App;
